/**
 * Plain-text 3-way merge for a single paragraph using diff-match-patch.
 * Returns merged text when clean; otherwise null (caller should emit a conflict).
 */

import DiffMatchPatchModule from 'diff-match-patch';

type DmpCtor = new () => {
  patch_make: (a: string, b: string) => unknown;
  patch_apply: (patches: unknown, text: string) => [string, boolean[]];
};

const DiffMatchPatch: DmpCtor =
  typeof DiffMatchPatchModule === 'function'
    ? (DiffMatchPatchModule as unknown as DmpCtor)
    : ((DiffMatchPatchModule as { diff_match_patch: DmpCtor }).diff_match_patch ??
      (DiffMatchPatchModule as unknown as DmpCtor));

const dmp = new DiffMatchPatch();

/**
 * Attempt a clean 3-way merge of plain text.
 * Returns the merged string, or `null` if patches conflict.
 */
export function threeWayTextMerge(base: string, mine: string, theirs: string): string | null {
  if (mine === theirs) return mine;
  if (mine === base) return theirs;
  if (theirs === base) return mine;

  // Patch base→mine onto theirs (which already includes base→theirs).
  const patchesMine = dmp.patch_make(base, mine);
  const [merged, results] = dmp.patch_apply(patchesMine, theirs);
  if (results.some((ok) => !ok)) {
    // Try the other direction: patch base→theirs onto mine.
    const patchesTheirs = dmp.patch_make(base, theirs);
    const [merged2, results2] = dmp.patch_apply(patchesTheirs, mine);
    if (results2.some((ok) => !ok)) return null;
    return merged2;
  }
  return merged;
}

/** Build a TipTap paragraph node with plain text (preserves attrs.id / type when provided). */
export function plainTextToParagraphNode(
  text: string,
  template: { type?: string; attrs?: Record<string, unknown> } | null
): {
  type: string;
  attrs?: Record<string, unknown>;
  content?: Array<{ type: 'text'; text: string }>;
} {
  const type = template?.type && typeof template.type === 'string' ? template.type : 'paragraph';
  const attrs = template?.attrs ? { ...template.attrs } : undefined;
  const trimmed = text;
  if (!trimmed) {
    return attrs ? { type, attrs, content: [] } : { type, content: [] };
  }
  return attrs
    ? { type, attrs, content: [{ type: 'text', text: trimmed }] }
    : { type, content: [{ type: 'text', text: trimmed }] };
}
